<template>
    <div class="assistent-card">
        <div class="assistent-card__header">
            <flex-container
                size="auto"
                align-items="center"
                gap="sm"
            >
                <div class="assistent-card__icon">
                    <svg-icon
                        :icon="WindowFlipIcon"
                        color="white"
                        size="sm"
                        is-custom-icon
                    />
                </div>
                <text-decorator size="lg" style="font-weight: 500">
                    {{ props.assistent.brand.websiteDomain }}
                </text-decorator>
            </flex-container>
            <flex-container
                size="auto"
                direction="column"
                gap="xs"
            >
                <text-decorator size="xxs">
                    Aantal pagina's
                </text-decorator>
                <div class="large">
                    {{ crawlResultsCount > 0 ? crawlResultsCount : '-' }}
                </div>
            </flex-container>
            <flex-container
                size="auto"
                direction="column"
                gap="xs"
            >
                <text-decorator size="xxs">
                    Laatste scan
                </text-decorator>
                <flex-container
                    gap="sm"
                    align-items="end"
                    no-wrap
                >
                    <template v-if="lastCrawledAt">
                        <div class="large">
                            {{ formatDate(lastCrawledAt) }}
                        </div>
                        <flex-container gap="xs" class="time">
                            <svg-icon
                                :icon="ClockIcon"
                                color="gray-300"
                                size="xxs"
                                is-custom-icon
                            />
                            {{ formatTime(lastCrawledAt) }}
                        </flex-container>
                    </template>
                    <div v-else class="large">
                        -
                    </div>
                </flex-container>
            </flex-container>
            <flex-container
                size="auto"
                direction="column"
                gap="xs"
            >
                <text-decorator size="xxs">
                    Laatste wijziging
                </text-decorator>
                <flex-container
                    gap="sm"
                    align-items="end"
                    no-wrap
                >
                    <template v-if="lastUpdatedAt">
                        <div class="large">
                            {{ formatDate(lastUpdatedAt) }}
                        </div>
                        <flex-container gap="xs" class="time">
                            <svg-icon
                                :icon="ClockIcon"
                                color="gray-300"
                                size="xxs"
                                is-custom-icon
                            />
                            {{ formatTime(lastUpdatedAt) }}
                        </flex-container>
                    </template>
                    <div v-else class="large">
                        -
                    </div>
                </flex-container>
            </flex-container>
            <flex-container
                size="auto"
                align-items="center"
                justify-content="end"
            >
                <button-text
                    @click="onClickOpenLive"
                    @keydown.enter="onClickOpenLive"
                >
                    <text-decorator size="xxs" color="gray-500">
                        Bekijk live
                    </text-decorator>
                    <div class="assistent-card__show-live-icon">
                        <button-plain-icon
                            :icon="ArrowRightIcon"
                            color="white"
                            size="xs"
                            is-custom-icon
                        />
                    </div>
                </button-text>
            </flex-container>
        </div>
        <div class="assistent-card__content">
            <div class="assistent-card__layer4">
                <button-text color="white" @click="onClickShowChats">
                    <text-decorator size="xs">
                        Bekijk alle gesprekken
                    </text-decorator>

                    <svg-icon
                        :icon="ArrowRightIcon"
                        color="white"
                        size="xxs"
                        is-custom-icon
                    />
                </button-text>

                <button-block
                    color="white"
                    without-loading
                    @click="onClickShowReport(assistent.id.value)"
                >
                    <svg-icon
                        :icon="ChartSimpleIcon"
                        :color="'primary'"
                        size="xs"
                        is-custom-icon
                    />
                    <text-decorator :color="'primary'">
                        Bekijk inzichten
                    </text-decorator>
                </button-block>
            </div>
            <div class="assistent-card__layer3">
                <assistent-statistics-graph
                    :data="graphData"
                    :padding-left="80"
                    :padding-right="80"
                />
            </div>
            <div class="assistent-card__layer2">
                <flex-container
                    direction="column"
                    align-items="center"
                    size="auto"
                >
                    <flex-container
                        direction="column"
                        align-items="center"
                        size="auto"
                        gap="xs"
                        no-wrap
                    >
                        <text-decorator
                            color="gray-500"
                            size="xs"
                            style="font-size: 333%"
                        >
                            {{ statistics?.current.newChatsCount ?? 0 }}
                        </text-decorator>
                        <text-decorator color="gray-500" size="xs">
                            {{ statistics?.current.newChatsCount == 1 ? 'gesprek' : 'gesprekken' }}
                        </text-decorator>
                    </flex-container>
                    <flex-container size="auto">
                        <choice-input
                            v-model="range"
                            :items="AIRange.values"
                        />
                    </flex-container>
                </flex-container>
            </div>
            <div class="assistent-card__layer1">
                <img
                    v-if="avatarSource"
                    class="assistent-card__avatar"
                    :src="avatarSource"
                    alt="Avatar"
                />
                <div class="assistent-card__settings-button">
                    <button-block-icon
                        v-if="isCompanyManager"
                        :icon="GearIcon"
                        size="xs"
                        is-custom-icon
                        @click="onClickSettings"
                    />
                </div>
                <div class="assistent-card__status" :class="statusClasses">
                    <template v-if="!AssistentStatus.READY.equals(assistent.status)">
                        {{ assistent.status.label }}
                    </template>
                    <template v-else-if="assistent.active">
                        Actief
                    </template>
                    <template v-else>
                        Inactief
                    </template>
                </div>
                <div class="assistent-card__name">
                    {{ assistent.name }}
                </div>
            </div>
        </div>
    </div>

    <assistent-generating-dialog ref="assistentGeneratingDialog" />
</template>

<script setup lang="ts">
    import { computed, onMounted, ref, toRaw, watch } from 'vue';
    import { formatDayMonth, formatDate, formatTime } from '@/shared/utils/date';
    import { useRouter } from 'vue-router';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import {
        type Assistent,
        CrawlResult,
        AssistentStatus,
        AvatarVariant,
        StatisticsForAssistent,
        GetStatisticsForAssistentCommand,
        AIRange,
        type AIScale,
        KeyflowRole,
    } from '@keyvue/one-api-client';

    import WindowFlipIcon from '@keyvue/one-awesome-icons/icons/regular/window-flip.svg?component';
    import ClockIcon from '@keyvue/one-awesome-icons/icons/regular/clock.svg?component';
    import GearIcon from '@keyvue/one-awesome-icons/icons/regular/gear.svg?component';
    import ArrowRightIcon from '@keyvue/one-awesome-icons/icons/regular/arrow-right.svg?component';
    import ChoiceInput from '@/ai/assistent/components/input/ChoiceInput.vue';
    import ChartSimpleIcon from '@keyvue/one-awesome-icons/icons/solid/chart-simple.svg?component';
    import AssistentGeneratingDialog from '@/ai/assistent/components/dialog/AssistentGeneratingDialog.vue';
    import AssistentStatisticsGraph from '@/ai/assistent/components/AssistentStatisticsGraph.vue';

    const router = useRouter();
    const { client, currentUser, getLazyResourceCollection } = useClient();

    const props = defineProps<{
        assistent: Assistent,
    }>();

    const assistentGeneratingDialog = ref<InstanceType<typeof AssistentGeneratingDialog>>();

    const isCompanyManager = computed(() => currentUser.value.keyflowRole?.equals(KeyflowRole.COMPANY_MANAGER));

    const range = ref(AIRange.THIS_WEEK);
    const statistics = ref<StatisticsForAssistent | undefined>();

    const lastCrawledAtCollection = getLazyResourceCollection(CrawlResult, {
        'order[crawledAt]': 'desc',
        'itemsPerPage': 1,
        'pagination': true,
        'brand': props.assistent.brand.id,
    });

    const lastUpdatedAtCollection = getLazyResourceCollection(CrawlResult, {
        'order[updatedAt]': 'desc',
        'itemsPerPage': 1,
        'pagination': true,
        'brand': props.assistent.brand.id,
    });

    const crawlResultsCount = computed(() => {
        if (lastCrawledAtCollection.total.value > 0) {
            return lastCrawledAtCollection.total.value;
        }

        if (lastUpdatedAtCollection.total.value > 0) {
            return lastUpdatedAtCollection.total.value;
        }

        return 0;
    });

    const lastCrawledAt = computed(() => {
        if (lastCrawledAtCollection.data.value?.length === 0) {
            return null;
        }

        return lastCrawledAtCollection.data.value?.[0].crawledAt;
    });

    const lastUpdatedAt = computed(() => {
        if (lastUpdatedAtCollection.data.value?.length === 0) {
            return null;
        }

        return lastUpdatedAtCollection.data.value?.[0].updatedAt;
    });

    const graphData = computed(() => {
        if (!statistics.value?.data) {
            return [];
        }

        return Object.entries(toRaw(statistics.value.data)).map(([key, v]) => {
            const value = v.newChatsCount as unknown as number;

            if (!statistics.value?.dataScale) {
                return {
                    value,
                    label: key,
                };
            }

            return {
                value,
                label: formatStatisticsLabel(
                    statistics.value.dataScale,
                    key,
                    value,
                ),

            };
        });
    });

    const statusClasses = computed(() => {
        return {
            'assistent-card__status': true,
            [`assistent-card__status--${props.assistent.status}`]: true,
            [`assistent-card__status--${props.assistent.active ? 'active' : 'inactive'}`]: true,
        };
    });

    const avatarSource = computed(() => {
        const variant = props.assistent.avatarVariant;

        if (!variant) {
            return null;
        }

        if (AvatarVariant.CUSTOM.equals(variant)) {
            return props.assistent.avatar?.url;
        }

        if (AvatarVariant.VARIANT1.equals(variant)) {
            return '/avatars/1.png';
        }

        if (AvatarVariant.VARIANT2.equals(variant)) {
            return '/avatars/2.png';
        }

        if (AvatarVariant.VARIANT3.equals(variant)) {
            return '/avatars/3.png';
        }

        return null;
    });

    function formatStatisticsLabel(scale: AIScale, key: string, value: number) {
        switch (scale) {
            case 'weeks': {
                const [year, week] = key.split('-').map(Number);
                return `<strong>${value}</strong> - week ${week}, ${year}`;
            }
            case 'days': return `<strong>${value}</strong> - ${formatDayMonth(key)}`;
            case 'hours': return `<strong>${value}</strong> - ${key} uur`;
            default: return `<strong>${value}</strong> - ${key}`;
        }
    }

    function onClickOpenLive() {
        if (!props.assistent.brand.websiteUrl) {
            return;
        }

        window.open(
            props.assistent.brand.websiteUrl,
            '_blank',
            'noopener,noreferrer',
        );
    }

    function onClickSettings() {
        if (props.assistent.isGenerating) {
            assistentGeneratingDialog.value?.dialog?.openOverlay();
            return;
        }

        router.push({
            name: 'assistent-edit',
            params: {
                assistentId: props.assistent.id.value,
            },
        });
    }

    function onClickShowChats() {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('last_visisted_assistent_id', props.assistent.id.value);
        }

        router.push({
            name: 'assistent-detail',
            params: {
                assistentId: props.assistent.id.value,
            },
        });
    }

    function onClickShowReport(assistentId: string) {
        router.push({
            name: 'assistent-report',
            params: {
                assistentId,
            },
        });
    }

    onMounted(() => {
        lastCrawledAtCollection.load();
        lastUpdatedAtCollection.load();
    });

    watch(range, async () => {
        statistics.value = await client.dispatch(
            new GetStatisticsForAssistentCommand(
                props.assistent.id,
                new AIRange(range.value.value),
            ),
        );
    }, { immediate: true });
</script>

<style scoped lang="scss">
    .assistent-card {
        background: var(--theme-colors-default--white);
        display: grid;
        grid-template-rows: auto auto;
        border-radius: size(sm);
        overflow: hidden;

        .small {
            font-size: 80%;
        }

        .large {
            font-size: 130%;
            font-weight: 500;
            white-space: nowrap;
        }

        .time {
            font-size: 80%;
            font-weight: 500;
        }

        &__avatar {
            position: absolute;
            object-fit: cover;
            object-position: center 30%;
            height: 100%;
            width: 100%;
        }

        &__settings-button {
            position: absolute;
            inset: 0;
            display: flex;
            padding: size(sm);
            align-items: center;
            justify-content: end;
            gap: size(sm);

            :deep(svg) {
                width: size(md);
                height: size(md);
            }
        }

        &__status {
            position: absolute;
            color: var(--theme-colors-default--white);
            font-size: size(sm);
            padding: size(sm);
            border-bottom-right-radius: size(xs);

            &--ready {
                background-color: var(--theme-colors-default--green-500);
            }

            &--active {
                background-color: var(--theme-colors-default--green-500);
            }

            &--inactive {
                background-color: var(--theme-colors-default--red-500);
            }

            &--crawling {
                background-color: var(--theme-colors-default--orange-500);
            }

            &--analyzing {
                background-color: var(--theme-colors-default--orange-500);
            }

            &--generating {
                background-color: var(--theme-colors-default--orange-500);
            }
        }

        &__name {
            position: absolute;
            bottom: 0;
            left: 0;
            padding: size(sm);
            color: var(--theme-colors-default--white);
            text-shadow: 0 0 .25rem rgba(0, 0, 0, 0.75);
            font-weight: 500;
            font-size: size(md);
            overflow: hidden;
            text-wrap: nowrap;
            text-overflow: ellipsis;
            width: calc(100% - 65px);
        }

        &__icon {
            width: size(lg);
            height: size(lg);
            background-color: var(--theme-colors-default--turquoise-500);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        &__show-live-icon {
            width: size(md);
            height: size(md);
            background: var(--theme-colors-default--gray-400);
            border-radius: 50%;
            transform: rotate(-45deg);
            display: flex;
            align-items: center;
            justify-content: center;

            :deep(svg) {
                width: size(sm);
                height: size(sm);
            }
        }

        &__header {
            display: grid;
            grid-template-columns: auto auto auto auto 1fr;
            gap: size(lg);
            padding: size(md);
            color: var(--theme-colors-default--gray-500);
            align-items: center;
        }

        &__content {
            position: relative;
            min-height: 170px;
            overflow: hidden;
            box-shadow: 3px 0 3px 0 rgba(0,0,0,.2);
        }

        &__layer1, &__layer2, &__layer3, &__layer4 {
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            overflow: hidden;
            background-color: var(--theme-colors-default--white);;
            box-shadow: size(xs) 0 10px -5px rgba(0,0,0,.2);
            display: flex;
        }

        &__layer1 {
            right: 80%;
            border-top-right-radius: 200px;
            border-bottom-right-radius: 200px;
            background: linear-gradient(to bottom, #d0e8f5, #dfe6ea);

            &::after {
                content: "";
                position: absolute;
                inset: 0;
                background: linear-gradient(to bottom, transparent 60%, rgba(0, 0, 0, 0.1) 100%);
                pointer-events: none;
            }
        }

        &__layer2 {
            right: 54%;
            padding-left: 20%;
            padding-right: 25px;
            border-top-right-radius: 200px;
            border-bottom-right-radius: 200px;
            justify-content: center;
            align-items: center;
        }

        &__layer3 {
            right: 17%;
            padding-left: 40%;
            border-top-right-radius: 200px;
            border-bottom-right-radius: 200px;
            justify-content: stretch;
            align-items: stretch;
        }

        &__layer4 {
            right: 0;
            padding-left: 83%;
            background: var(--theme-colors-default--primary);
            justify-content: center;
            align-items: center;
            flex-direction: column;
            gap: size(md);
        }
    }
</style>
